import { formatImgUrl, ImgUrlProps } from 'jamespot-user-api';
import { JRCTypography } from 'components/JRCTypography/JRCTypography';
import * as React from 'react';
import styled from 'styled-components';
import { getColor } from 'styles/theme';
import { css } from 'styled-components';
import { JRCCard } from './JRCCard';
import { DashedFocusBorderAbsolute } from '../Common/DashedFocusBorder';
import { useEffect, useRef, useState } from 'react';

const ASPECTS = {
    '4:3': 75,
    '5:4': 80,
    '16:9': 56.25,
    '1': 100,
    '3:4': 133,
    '4:5': 125,
    '9:16': 178,
};

export type ActionsPosition = {
    x?: 'left' | 'right';
    y?: 'top' | 'bottom' | 'under';
};

export type JRCCardImgProps = {
    color?: string;
    icon?: boolean;
    img?: string;
    imgFromObject?: ImgUrlProps;
    title: string;
    content?: React.ReactNode;
    aspect?: keyof typeof ASPECTS;
    href?: string;
    onClick?: (event: any) => any;
    actions?: {
        children: React.ReactNode;
        position?: ActionsPosition;
    };
    lazyLoading?: boolean;
};
const RelativeCard = styled(JRCCard)`
    position: relative;
`;

const HeaderStyle = css<{ color?: string; img?: string; aspect: number; icon?: boolean }>`
    ${(props) => props.img && `background-image: url("${props.img}")`};
    background-repeat: no-repeat;
    background-size: ${(props) => (props.icon ? '30%' : 'cover')};
    background-position: center;
    ${(props) => props.color && `background-color: ${getColor(props.theme, props.color)}`};
    padding-bottom: ${(props) => props.aspect}%;
    display: block;
    transition: all 0.2s;
    ${DashedFocusBorderAbsolute}

    :hover {
        transform: scale(1.05);
    }
`;
const HeaderCardLink = styled.a<{ color?: string; img?: string; aspect: number; icon?: boolean }>`
    ${HeaderStyle}
`;

const HeaderCardButton = styled.button<{ color?: string; img?: string; aspect: number; icon?: boolean }>`
    ${HeaderStyle}
`;

const ContentCard = styled.div<{}>`
    background: ${(props) => props.theme.color.grey0};
    padding: 8px 16px 16px;
`;

export const Actions = styled.div<{ position?: ActionsPosition }>`
    position: absolute;
    ${(props) => (props?.position?.x === 'left' || props?.position?.y === 'under' ? 'left: 4px' : 'right: 4px')};
    ${(props) =>
        props?.position?.y === 'bottom' ? 'bottom: 4px' : props?.position?.y === 'top' ? 'top: 4px' : 'bottom: -50px'};
    display: flex;
    gap: 4px;
    z-index: 1;
`;

export const JRCCardImg = (props: JRCCardImgProps) => {
    const anchorRef = useRef<HTMLAnchorElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isInViewport, setIsInViewport] = useState<boolean>();

    useEffect(() => {
        const intersectObserver = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                setIsInViewport(true);
            }
        });
        if (props.lazyLoading) {
            if (anchorRef.current) {
                intersectObserver.observe(anchorRef.current);
            }
            if (buttonRef.current) {
                intersectObserver.observe(buttonRef.current);
            }
        }

        return () => {
            intersectObserver.disconnect();
        };
    }, [props.lazyLoading]);

    const imgUrl = props.imgFromObject ? formatImgUrl(props.imgFromObject) : props.img;
    const displayImage = !props.lazyLoading || isInViewport;
    return (
        <RelativeCard padding="mini">
            <div style={{ overflow: 'hidden' }}>
                {props.href && (
                    <HeaderCardLink
                        href={props.href}
                        color={props.color}
                        img={displayImage ? imgUrl : undefined}
                        icon={props.icon}
                        aspect={ASPECTS[props.aspect ?? '4:3']}
                        ref={anchorRef}
                    />
                )}
                {!props.href && props.onClick && (
                    <HeaderCardButton
                        onClick={props.onClick}
                        color={props.color}
                        icon={props.icon}
                        img={displayImage ? imgUrl : undefined}
                        aspect={ASPECTS[props.aspect ?? '4:3']}
                        ref={buttonRef}
                    />
                )}
            </div>
            <ContentCard>
                <JRCTypography weight="medium">{props.title}</JRCTypography>
                {props.content}
            </ContentCard>
            {props.actions && <Actions position={props.actions.position}>{props.actions.children}</Actions>}
        </RelativeCard>
    );
};
